import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, KeyRound, ShieldCheck } from 'lucide-react';
import PortalPageShell from '../components/PortalPageShell';
import { LOGIN } from '../constants/testIds';
import { loginUser } from '../lib/portalApi';
import SocialLogin from '../components/SocialLogin';
import { useT } from '../i18n/LanguageContext';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const [values, setValues] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nextPath = location.state?.from?.pathname || '/account';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await loginUser({
        email: values.email.trim(),
        password: values.password,
      });

      navigate(nextPath, { replace: true });
    } catch (requestError) {
      setError(requestError.message || t('auth.loginError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PortalPageShell
      eyebrow={t('auth.loginEyebrow')}
      title={t('auth.loginTitle')}
      description={t('auth.loginDesc')}
      widthClassName="max-w-5xl"
    >
      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <section className="rounded-[28px] border border-white/70 bg-white/90 p-8 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.28)] backdrop-blur">
          {new URLSearchParams(location.search).get('expired') ? (
            <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
              {t('auth.sessionExpired')}
            </div>
          ) : null}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="login-email" className="block text-sm font-semibold text-[#14213d] mb-2">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                value={values.email}
                data-testid={LOGIN.emailInput}
                onChange={(event) => setValues((current) => ({ ...current, email: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10"
                placeholder="tu@email.com"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <label htmlFor="login-password" className="block text-sm font-semibold text-[#14213d]">
                  {t('auth.password')}
                </label>
                <Link
                  to="/contact"
                  data-testid={LOGIN.forgotPasswordLink}
                  className="text-sm font-medium text-[#1fa5a3] hover:text-[#158582]"
                >
                  {t('auth.forgot')}
                </Link>
              </div>
              <input
                id="login-password"
                type="password"
                value={values.password}
                data-testid={LOGIN.passwordInput}
                onChange={(event) => setValues((current) => ({ ...current, password: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10"
                placeholder={t('auth.passwordPlaceholder')}
                autoComplete="current-password"
                required
              />
            </div>

            {error ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              data-testid={LOGIN.submitButton}
              disabled={isSubmitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#14213d] px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-[#0f1a31] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <KeyRound className="h-4 w-4" />
              {isSubmitting ? t('auth.loggingIn') : t('auth.login')}
            </button>
          </form>

          <SocialLogin onAuthed={() => navigate(nextPath, { replace: true })} />

          <div className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 text-sm text-slate-600">
            <span>{t('auth.noAccount')}</span>
            <Link
              to="/register"
              data-testid={LOGIN.registerLink}
              className="inline-flex items-center gap-2 font-semibold text-[#1fa5a3]"
            >
              {t('auth.createAccount')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        <aside className="rounded-[28px] bg-[#0b1c26] p-8 text-white shadow-[0_28px_80px_-36px_rgba(11,28,38,0.5)]">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
            <ShieldCheck className="h-6 w-6 text-[#c8a25a]" />
          </div>
          <h2 className="mt-6 text-2xl font-bold">{t('auth.benefitsTitle')}</h2>
          <ul className="mt-5 space-y-4 text-sm leading-6 text-white/78">
            <li>{t('auth.benefit1')}</li>
            <li>{t('auth.benefit2')}</li>
            <li>{t('auth.benefit3')}</li>
          </ul>
        </aside>
      </div>
    </PortalPageShell>
  );
}
